// Hybrid Service: Nutzt API wenn verfügbar, sonst IndexedDB
import { apiService } from './apiService';
import * as dbService from './dbService';
import { downloadMissingImagesInBackground, syncDocumentationForOffline } from './syncService';
import { IMAGE_CACHE, clearImageCacheStorage, getCatalogImageStatus } from './imageCache';
import { getSessionToken } from './authService';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class DataService {
  constructor() {
    this.useApi = false;
    this.apiChecked = false;
  }

  async checkApiAvailability() {
    if (this.apiChecked) {
      return this.useApi;
    }
    
    try {
      this.useApi = await apiService.healthCheck();
      this.apiChecked = true;
      console.log(`📡 Datenquelle: ${this.useApi ? 'Backend-API' : 'Lokale IndexedDB'}`);
      return this.useApi;
    } catch {
      this.useApi = false;
      this.apiChecked = true;
      return false;
    }
  }

  // Devices
  async getDevices(filters = null) {
    await this.checkApiAvailability();

    const typeFilter = typeof filters === 'string' ? filters : filters?.type ?? null;
    
    if (this.useApi) {
      try {
        const devices = await apiService.getDevices(filters);
        const devicesWithImages = await Promise.all(devices.map(async device => {
          if (device.image_filename) {
            return {
              ...device,
              imageUrl: await apiService.getDeviceImageUrl(device.id, device.image_filename)
            };
          }
          return device;
        }));
        // Cache in IndexedDB für offline-Nutzung
        this.cacheDevices(devicesWithImages);
        return devicesWithImages;
      } catch (error) {
        console.warn('API fehlgeschlagen, fallback zu IndexedDB');
        this.useApi = false;
      }
    }
    
    // Fallback auf IndexedDB
    const db = await dbService.initDatabase();
    
    // Prüfe ob devices Store existiert
    if (!db.objectStoreNames.contains('devices')) {
      console.error('devices ObjectStore existiert nicht - Datenbank muss synchronisiert werden');
      return [];
    }
    
    let devices = await db.getAll('devices');
    devices = devices.filter(d => !d.documentation_only);
    if (typeof filters === 'object' && filters) {
      if (filters.customerId) {
        devices = devices.filter(d => Number(d.customer_id) === Number(filters.customerId));
      }
      if (filters.categoryId) {
        devices = devices.filter(d => Number(d.category_id) === Number(filters.categoryId));
      } else if (filters.type) {
        devices = devices.filter(d => d.type === filters.type);
      }
    } else if (typeFilter) {
      devices = devices.filter(d => d.type === typeFilter);
    }

    const devicesWithImages = await Promise.all(devices.map(async device => {
      if (device.image_filename) {
        return {
          ...device,
          imageUrl: await apiService.getDeviceImageUrl(device.id, device.image_filename)
        };
      }
      return device;
    }));

    return devicesWithImages;
  }

  async cacheDevices(devices) {
    try {
      const db = await dbService.initDatabase();
      const tx = db.transaction('devices', 'readwrite');
      
      for (const device of devices) {
        await tx.objectStore('devices').put(device);
      }
      
      await tx.done;
    } catch (error) {
      console.warn('Failed to cache devices:', error);
    }
  }

  async getDevice(id) {
    if (this.useApi) {
      try {
        return await apiService.getDevice(id);
      } catch (error) {
        console.warn('API fehlgeschlagen');
        this.useApi = false;
      }
    }
    
    const devices = await this.getDevices();
    return devices.find(d => d.id === id);
  }

  // Prüfe und synchronisiere automatisch beim Login
  async checkAndSync(forceCheck = false) {
    if (!navigator.onLine) {
      console.log('⚠️ Offline - Synchronisation übersprungen');
      return { success: false, reason: 'offline' };
    }

    try {
      await this.checkApiAvailability();
      
      if (!this.useApi) {
        return { success: false, reason: 'api_unavailable' };
      }

      // Hole Server-Metadaten
      const serverMeta = await apiService.getMetadata();
      const localMeta = await this.getSyncMetadata();

      // Prüfe ob Sync nötig ist
      const serverDataNewer = serverMeta.lastUpdate && localMeta.lastSync && 
                             new Date(serverMeta.lastUpdate) > new Date(localMeta.lastSync);
      
      // Auto-Sync nach 60 Minuten
      const lastSyncAge = localMeta.lastSync ? 
                         (Date.now() - new Date(localMeta.lastSync).getTime()) / 1000 / 60 : 
                         999999; // Sehr alt wenn noch nie synchronisiert
      const syncTooOld = lastSyncAge > 60;

      const needsSync = !localMeta.lastSync || serverDataNewer || (forceCheck && syncTooOld);

      if (needsSync) {
        if (syncTooOld) {
          console.log('🔄 Letzte Synchronisation > 60 Minuten - Aktualisiere...');
        } else {
          console.log('🔄 Daten veraltet - Synchronisiere...');
        }
        await this.syncDevices();
        await this.syncParts();
        await this.updateSyncMetadata();
        return { success: true, synced: true, lastSync: new Date().toISOString() };
      } else {
        console.log('✅ Daten sind aktuell');
        return { success: true, synced: false, lastSync: localMeta.lastSync };
      }
    } catch (error) {
      console.error('Auto-sync check failed:', error);
      return { success: false, error: error.message };
    }
  }

  async getSyncMetadata() {
    try {
      const db = await dbService.initDatabase();
      
      // Hole beide Sync-Timestamps (full und delta)
      const lastFullSync = await db.get('sync_metadata', 'last_full_sync');
      const lastDeltaSync = await db.get('sync_metadata', 'last_delta_sync');
      
      // Nimm den neuesten der beiden
      let lastSync = null;
      if (lastFullSync?.value && lastDeltaSync?.value) {
        lastSync = new Date(lastFullSync.value) > new Date(lastDeltaSync.value) 
          ? lastFullSync.value 
          : lastDeltaSync.value;
      } else if (lastFullSync?.value) {
        lastSync = lastFullSync.value;
      } else if (lastDeltaSync?.value) {
        lastSync = lastDeltaSync.value;
      }
      
      return { lastSync };
    } catch (error) {
      console.error('Failed to get sync metadata:', error);
      return { lastSync: null };
    }
  }

  async updateSyncMetadata() {
    try {
      const db = await dbService.initDatabase();
      const timestamp = new Date().toISOString();
      
      // Setze last_full_sync (wird von AutoSync und getSyncMetadata verwendet)
      await db.put('sync_metadata', {
        key: 'last_full_sync',
        value: timestamp,
        updatedAt: timestamp
      });
      
      // Legacy: Setze auch den alten Key für Rückwärtskompatibilität
      await db.put('sync_metadata', {
        key: 'last_sync',
        lastSync: timestamp,
        value: timestamp
      });
    } catch (error) {
      console.warn('Failed to update sync metadata:', error);
    }
  }

  // Sync devices from API to IndexedDB
  async syncDevices() {
    try {
      const devices = await apiService.getDevices();
      const db = await dbService.initDatabase();
      
      // Clear and repopulate
      const tx = db.transaction('devices', 'readwrite');
      await tx.objectStore('devices').clear();
      
      for (const device of devices) {
        await tx.objectStore('devices').put(device);
      }
      
      await tx.done;
      console.log(`✅ ${devices.length} Geräte synchronisiert`);
      return { success: true, count: devices.length };
    } catch (error) {
      console.error('Sync failed:', error);
      throw error;
    }
  }

  // Sync parts from API to IndexedDB
  async syncParts(includeImages = false) {
    try {
      const parts = await apiService.getParts();
      const db = await dbService.initDatabase();
      
      // Clear and repopulate
      const tx = db.transaction('parts', 'readwrite');
      await tx.objectStore('parts').clear();
      
      for (const part of parts) {
        await tx.objectStore('parts').put(part);
      }
      
      await tx.done;
      console.log(`✅ ${parts.length} Ersatzteile synchronisiert`);
      
      // Cache images for offline use - nur wenn explizit gewünscht
      if (includeImages) {
        console.log('📷 Bilder werden synchronisiert...');
        await this.cachePartImages(parts);
      } else {
        console.log('📷 Bilder-Synchronisation übersprungen (nur bei manueller Sync)');
      }
      
      return { success: true, count: parts.length };
    } catch (error) {
      console.error('Parts sync failed:', error);
      throw error;
    }
  }

  // Full Sync: Löscht alle alten Daten und lädt alles neu (außer Bilder optional)
  async fullSync(includeImages = false) {
    if (!navigator.onLine) {
      return { success: false, error: 'Keine Internetverbindung' };
    }

    try {
      await this.checkApiAvailability();
      
      if (!this.useApi) {
        return { success: false, error: 'API nicht verfügbar' };
      }

      console.log(`🔄 Vollständige Synchronisation gestartet (Bilder: ${includeImages ? 'Ja' : 'Nein'})...`);

      // 1. Geräte synchronisieren
      const devicesResult = await this.syncDevices();
      
      // 2. Ersatzteile synchronisieren (mit oder ohne Bilder)
      const partsResult = await this.syncParts(includeImages);

      // 3. Dokumentationen synchronisieren
      console.log('📚 Dokumentationen synchronisieren...');
      await syncDocumentationForOffline();
      
      // 4. Sync-Metadaten aktualisieren
      await this.updateSyncMetadata();
      
      console.log('✅ Vollständige Synchronisation erfolgreich abgeschlossen');
      
      return { 
        success: true, 
        devicesCount: devicesResult.count,
        partsCount: partsResult.count
      };
    } catch (error) {
      console.error('Full sync failed:', error);
      return { success: false, error: error.message || 'Synchronisation fehlgeschlagen' };
    }
  }

  async clearDevices() {
    const db = await dbService.initDatabase();
    const tx = db.transaction('devices', 'readwrite');
    await tx.objectStore('devices').clear();
    await tx.done;
    console.log('✅ Geräte gelöscht');
  }

  // Categories
  async getCategories() {
    await this.checkApiAvailability();
    
    if (this.useApi) {
      try {
        return await apiService.getCategories();
      } catch (error) {
        console.warn('API fehlgeschlagen, fallback zu IndexedDB');
        this.useApi = false;
      }
    }
    
    const db = await dbService.initDatabase();
    return await db.getAll('categories');
  }

  async getCategory(id) {
    const categories = await this.getCategories();
    return categories.find(cat => cat.id === id);
  }

  // Parts
  async getParts(filters = {}) {
    await this.checkApiAvailability();
    
    if (this.useApi) {
      try {
        const parts = await apiService.getParts(filters);
        // Füge imageUrl für Parts mit Bildern hinzu
        const partsWithImages = await Promise.all(parts.map(async part => {
          if (part.image_filename) {
            return {
              ...part,
              imageUrl: await apiService.getPartImageUrl(part.id, part.image_filename)
            };
          }
          return part;
        }));
        // Cache in IndexedDB für offline-Nutzung
        this.cacheParts(partsWithImages);
        return partsWithImages;
      } catch (error) {
        console.warn('API fehlgeschlagen, fallback zu IndexedDB');
        this.useApi = false;
      }
    }
    
    const db = await dbService.initDatabase();
    let parts = await db.getAll('parts');
    
    // Filter lokal anwenden
    if (filters.deviceId) {
      parts = parts.filter(p => p.device_ids?.includes(parseInt(filters.deviceId)));
    }
    if (filters.categoryId) {
      parts = parts.filter(p => p.category_ids?.includes(parseInt(filters.categoryId)));
    }
    if (filters.category) {
      // Legacy support: Filter by category type
      parts = parts.filter(p => p.category_types?.includes(filters.category));
    }
    if (filters.search) {
      const keywords = filters.search.toLowerCase().trim().split(/\s+/).filter(Boolean);
      parts = parts.filter(p => {
        const targetText = `${p.name || ''} ${p.article_number || ''} ${p.description || ''} ${p.category_names || ''} ${p.device_names || ''}`.toLowerCase();
        return keywords.every(kw => targetText.includes(kw));
      });
    }
    
    // Füge imageUrl auch für gecachte Parts hinzu
    const partsWithImages = await Promise.all(parts.map(async part => {
      if (part.image_filename) {
        return {
          ...part,
          imageUrl: await apiService.getPartImageUrl(part.id, part.image_filename)
        };
      }
      return part;
    }));
    
    return partsWithImages;
  }

  async cachePartImages(parts) {
    // Cache images as blobs for offline use
    try {
      const { getSessionToken } = await import('./authService');
      const token = await getSessionToken();
      
      if (!token) return;

      for (const part of parts) {
        if (part.image_filename) {
          try {
            const response = await fetch(`${apiService.API_BASE_URL}/parts/${part.id}/image?token=${token}`);
            if (response.ok) {
              const blob = await response.blob();
              const db = await dbService.initDatabase();
              await db.put('part_images', {
                partId: part.id,
                blob: blob,
                timestamp: Date.now()
              });
            }
          } catch (error) {
            console.warn(`Failed to cache image for part ${part.id}:`, error);
          }
        }
      }
      console.log('✅ Bilder für Offline-Nutzung gecacht');
    } catch (error) {
      console.warn('Failed to cache images:', error);
    }
  }

  async cacheParts(parts) {
    try {
      const db = await dbService.initDatabase();
      const tx = db.transaction('parts', 'readwrite');
      
      for (const part of parts) {
        await tx.objectStore('parts').put(part);
      }
      
      await tx.done;
    } catch (error) {
      console.warn('Failed to cache parts:', error);
    }
  }

  async getPart(id) {
    if (this.useApi) {
      try {
        return await apiService.getPart(id);
      } catch (error) {
        console.warn('API fehlgeschlagen, fallback zu IndexedDB');
        this.useApi = false;
      }
    }
    
    const db = await dbService.initDatabase();
    return await db.get('parts', id);
  }


  async getCart() {
    const db = await dbService.initDatabase();
    return await db.getAll('cart');
  }

  async addToCart(item) {
    if (navigator.onLine) {
      try {
        const check = await apiService.get(`/parts/${item.partId || item.id}/shipping-check`);
        if (!check.hasShippingMethod) {
          throw new Error('Ersatzteil kann nicht hinzugefügt werden, da für den Kunden kein Versandweg konfiguriert ist.');
        }
      } catch (error) {
        if (error.message.includes('Versandweg konfiguriert ist')) {
          throw error;
        }
        console.warn('Shipping check failed, allowing cart addition anyway', error);
      }
    }

    const db = await dbService.initDatabase();
    const existingItems = await db.getAll('cart');
    // When a size/gender is selected, only consider it a duplicate if same partId AND same size AND same gender
    const existing = existingItems.find(i =>
      i.partId === (item.partId || item.id) &&
      (i.selectedSize || null) === (item.selectedSize || null) &&
      (i.selectedGender || null) === (item.selectedGender || null)
    );
    
    if (existing) {
      existing.quantity += item.quantity || 1;
      await db.put('cart', existing);
      return existing;
    } else {
      const newItem = {
        id: `cart-${Date.now()}`,
        partId: item.partId || item.id,
        name: item.name,
        articleNumber: item.articleNumber || item.article_number,
        description: item.description,
        quantity: item.quantity || 1,
        selectedSize: item.selectedSize || null,
        selectedGender: item.selectedGender || null,
        supplier: item.supplier || null,
        supplier_link: item.supplier_link || item.supplierLink || null,
        addedAt: new Date().toISOString()
      };
      await db.add('cart', newItem);
      return newItem;
    }
  }

  async updateCartItem(id, updates) {
    const db = await dbService.initDatabase();
    const item = await db.get('cart', id);
    if (item) {
      Object.assign(item, updates);
      await db.put('cart', item);
    }
    return item;
  }

  async removeFromCart(id) {
    const db = await dbService.initDatabase();
    await db.delete('cart', id);
  }

  async clearCart() {
    const db = await dbService.initDatabase();
    const tx = db.transaction('cart', 'readwrite');
    await tx.objectStore('cart').clear();
    await tx.done;
  }

  // Dispatcher Cart Funktionen
  async getDispatcherCart() {
    const db = await dbService.initDatabase();
    return await db.getAll('dispatcher_cart');
  }

  async addToDispatcherCart(item) {
    if (navigator.onLine) {
      try {
        const check = await apiService.get(`/parts/${item.partId || item.id}/shipping-check`);
        if (!check.hasShippingMethod) {
          throw new Error('Ersatzteil kann nicht hinzugefügt werden, da für den Kunden kein Versandweg konfiguriert ist.');
        }
      } catch (error) {
        if (error.message.includes('Versandweg konfiguriert ist')) {
          throw error;
        }
        console.warn('Shipping check failed, allowing cart addition anyway', error);
      }
    }

    const db = await dbService.initDatabase();
    const newItem = {
      ...item,
      uniqueId: `${item.partId || item.id}_${Date.now()}`,
      quantity: item.quantity || 1,
      ticketNumber: item.ticketNumber || '',
      addedAt: new Date().toISOString()
    };
    await db.add('dispatcher_cart', newItem);
    return newItem;
  }

  async updateDispatcherCartItem(uniqueId, updates) {
    const db = await dbService.initDatabase();
    const item = await db.get('dispatcher_cart', uniqueId);
    if (item) {
      Object.assign(item, updates);
      await db.put('dispatcher_cart', item);
    }
    return item;
  }

  async removeFromDispatcherCart(uniqueId) {
    const db = await dbService.initDatabase();
    await db.delete('dispatcher_cart', uniqueId);
  }

  async clearDispatcherCart() {
    const db = await dbService.initDatabase();
    const tx = db.transaction('dispatcher_cart', 'readwrite');
    await tx.objectStore('dispatcher_cart').clear();
    await tx.done;
  }

  // Orders - MUSS online sein, keine lokale Speicherung
  async createOrder(orderData) {
    if (!navigator.onLine) {
      throw new Error('Keine Internetverbindung. Bestellungen können nur online erstellt werden.');
    }
    
    try {
      console.log('Creating order via API...', orderData);
      const result = await apiService.createOrder(orderData);
      console.log('Order created successfully via API:', result);
      return result;
    } catch (error) {
      console.error('API create order failed:', error);
      if (error.name === 'AbortError') {
        // Server verarbeitet die Bestellung evtl. trotzdem weiter – Doppelbestellung vermeiden
        const abortError = new Error('Der Server hat nicht rechtzeitig geantwortet. Die Bestellung wurde möglicherweise trotzdem übermittelt. Bitte prüfen Sie Ihr E-Mail-Postfach, bevor Sie erneut bestellen.');
        abortError.isTimeout = true;
        throw abortError;
      }
      throw error;
    }
  }

  // Info
  async getInfo() {
    if (this.useApi) {
      try {
        return await apiService.getInfo();
      } catch (error) {
        console.warn('API fehlgeschlagen');
        this.useApi = false;
      }
    }
    
    const db = await dbService.initDatabase();
    const parts = await db.getAll('parts');
    const categories = await db.getAll('categories');
    const orders = await db.getAll('orders');
    
    return {
      parts: parts.length,
      categories: categories.length,
      orders: orders.length,
      database: 'IndexedDB (lokal)',
      host: 'Browser'
    };
  }

  // Status des Offline-Bildspeichers (Service-Worker-Cache, siehe services/imageCache.js)
  async getImageCacheStatus() {
    try {
      const db = await dbService.initDatabase();
      const [parts, devices, customers] = await Promise.all([
        db.getAll('parts'),
        db.getAll('devices'),
        db.objectStoreNames.contains('customers') ? db.getAll('customers') : []
      ]);
      const token = await getSessionToken();
      const { total, cached } = await getCatalogImageStatus({ parts, devices, customers }, API_BASE_URL, token);
      let bytes = null;
      try {
        if (typeof caches !== 'undefined') {
          const cache = await caches.open(IMAGE_CACHE);
          const responses = await Promise.all((await cache.keys()).map((k) => cache.match(k)));
          const sizes = await Promise.all(responses.map(async (r) => (r ? (await r.clone().blob()).size : 0)));
          bytes = sizes.reduce((sum, n) => sum + n, 0);
        }
      } catch { /* Größe optional */ }
      return {
        totalParts: parts.length,
        partsWithImages: parts.filter((p) => p.image_filename).length,
        totalDevices: devices.length,
        devicesWithImages: devices.filter((d) => d.image_filename).length,
        customersWithImages: customers.filter((c) => c.image_filename).length,
        totalImages: total,
        cachedImages: cached,
        missingImages: Math.max(0, total - cached),
        cacheComplete: total === cached,
        percentageCached: total > 0 ? Math.min(100, Math.round((cached / total) * 100)) : 100,
        bytes,
        swActive: Boolean(navigator.serviceWorker?.controller)
      };
    } catch (error) {
      console.error('Failed to get image cache status:', error);
      return { totalParts: 0, partsWithImages: 0, totalDevices: 0, devicesWithImages: 0, customersWithImages: 0, totalImages: 0, cachedImages: 0, missingImages: 0, cacheComplete: false, percentageCached: 0, bytes: null, swActive: false };
    }
  }

  // Bild-Cache leeren (zurücksetzen) – Service-Worker-Cache und Altbestand in IndexedDB
  async clearImageCache() {
    await clearImageCacheStorage();
    const db = await dbService.initDatabase();
    for (const store of ['part_images', 'device_images']) {
      if (db.objectStoreNames.contains(store)) await db.clear(store);
    }
    return { success: true };
  }

  // Alle fehlenden Bilder laden (manuell ausgelöst → auch über Mobilfunk)
  async cacheAllMissingImages() {
    if (!navigator.onLine) throw new Error('Keine Internetverbindung');
    return downloadMissingImagesInBackground({ force: true });
  }
}

export const dataService = new DataService();
export default dataService;
